import EyeIcons from '@/icons/EyeIcons'
import EyeOffIcons from '@/icons/EyeOffIcons'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

export type PrivacyFieldType = 'uuid' | 'email' | 'phone' | 'custom'

interface PrivacyFieldProps {
    value: string
    label?: string
    type?: PrivacyFieldType
    maskPattern?: string
    className?: string
    toggleable?: boolean
    onReveal?: (value: string) => void | Promise<void>
    onHide?: (value: string) => void | Promise<void>
    defaultVisible?: boolean
}

/** Mask value based on field type */
const getMaskedValue = (
    type: PrivacyFieldType,
    originalValue: string,
    maskPattern?: string
): string => {
    if (maskPattern) return maskPattern

    switch (type) {
        case 'uuid':
            return '••••••••-••••-••••-••••-••••••••••••'
        case 'email': {
            const [username, domain] = originalValue.split('@')
            if (!domain) return '••••••••••@••••••••••'
            const visiblePart = username.substring(0, 3)
            const maskedPart = '•'.repeat(Math.max(username.length - 3, 0))
            return `${visiblePart}${maskedPart}@${domain}`
        }
        case 'phone': {
            const visiblePart = originalValue.substring(0, 3)
            const maskedPart = originalValue.substring(3).replace(/\d/g, '•')
            return visiblePart + maskedPart
        }
        default:
            return '••••••••••••••••••••'
    }
}

const PrivacyField: React.FC<PrivacyFieldProps> = ({
    value,
    label,
    type = 'custom',
    maskPattern,
    className = '',
    toggleable = false,
    onReveal,
    onHide,
    defaultVisible = false,
}) => {
    const { t } = useTranslation()
    const [isVisible, setIsVisible] = useState(defaultVisible)

    const handleReveal = async () => {
        if (onReveal) await onReveal(value)
        setIsVisible(true)
    }

    const handleHide = async () => {
        if (onHide) await onHide(value)
        setIsVisible(false)
    }

    const maskedValue = getMaskedValue(type, value, maskPattern)

    return (
        <div className={`flex flex-col ${className} gap-0.5 min-w-0`}>
            {label && (
                <span className="text-xs font-medium text-muted-foreground">
                    {label}
                </span>
            )}
            <div
                className="relative max-w-full overflow-hidden"
                onClick={isVisible && toggleable ? handleHide : undefined}
                style={
                    isVisible && toggleable ? { cursor: 'pointer' } : undefined
                }
                title={
                    isVisible && toggleable
                        ? t('click-to-hide', 'Hide')
                        : undefined
                }
            >
                <span className="text-[11px] text-foreground/70 font-mono px-1.5 py-1 bg-muted/50 rounded-sm border border-border/50 flex items-center leading-tight truncate">
                    {isVisible ? value : maskedValue}
                </span>
                {!isVisible && (
                    <div
                        className="absolute inset-0 flex items-center justify-center bg-muted/80 rounded cursor-pointer hover:bg-muted transition-colors"
                        onClick={handleReveal}
                    >
                        <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                            <EyeIcons className="w-3 h-3" />
                            {t('click-to-reveal')}
                        </span>
                    </div>
                )}
                {isVisible && toggleable && (
                    <div className="absolute inset-0 flex items-center justify-center bg-muted/80 rounded cursor-pointer opacity-0 hover:opacity-100 transition-opacity">
                        <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                            <EyeOffIcons className="w-3 h-3" />
                        </span>
                    </div>
                )}
            </div>
        </div>
    )
}

export default PrivacyField
